/****************************************************************
 * CODE Product Configurator
 ****************************************************************/

import { formatMoney } from '@shopify/theme-currency';
import './autocomplete-input.js';

class ProductConfigurator extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      printColor: 'black',
      numberImageSources: [],
      blacklistNumbers: [],
      blacklistNames: [],
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Create events
    this.changeEvent = new Event('change');

    // Scope global variables
    this.currentSku = 'notdefined';
    this.nameFitAnimationFrame = null;
    this.nameFitLifecycleId = 0;
    this.nameFitResizeObserver = null;

    // Scope custom elements
    this.productMediaElement = this.querySelector('product-media');
    this.variantRadiosElement = this.querySelector('variant-radios');
    this.productFormElement = this.querySelector('product-form');
    this.cartNotification = document.querySelector('cart-notification');
    this.cartDrawer = document.querySelector('cart-drawer');

    // Scope elements
    this.mediaWrapper = this.querySelector('[data-configurator-media-wrapper]');

    this.printWrapper = this.querySelector('[data-configurator-print-wrapper]');
    this.printOptions = this.printWrapper?.querySelectorAll('[data-configurator-print-option]');
    this.printValueInput = this.printWrapper?.querySelector('[data-configurator-print-value]');
    this.printClublogoValueInput = this.printWrapper?.querySelector(
      '[data-configurator-print-clublogo-value]',
    );
    this.printAcknowledgementWrapper = this.printWrapper?.querySelector(
      '[data-configurator-print-acknowledgement-wrapper]',
    );
    this.printAcknowledgementInput = this.printWrapper?.querySelector(
      '[data-configurator-print-acknowledgement]',
    );
    this.printAcknowledgementLabel = this.printWrapper?.querySelector(
      '[data-configurator-print-acknowledgement-label]',
    );
    this.printAcknowledgementError = this.printWrapper?.querySelector(
      '[data-configurator-print-acknowledgement-error]',
    );

    this.playerInputWrapper = this.querySelector('[data-configurator-player-input-wrapper]');
    this.playerInput = this.playerInputWrapper?.querySelector('[data-configurator-player-input]');
    this.playerInputButton = this.playerInputWrapper?.querySelector(
      '[data-configurator-player-input-button]',
    );
    this.playerInputSelect = this.playerInputWrapper?.querySelector(
      '[data-configurator-player-input-select]',
    );
    this.playerInputOptions = this.playerInputWrapper?.querySelectorAll(
      '[data-configurator-player-input-option]',
    );

    // Autocomplete inputs for flocking options
    this.autocompleteInputs = this.querySelectorAll('autocomplete-input');

    this.customInputWrapper = this.querySelector('[data-configurator-custom-input-wrapper]');
    // Get flocking mode from wrapper data attribute, default to 'jersey' for backward compatibility
    this.flockingMode = this.customInputWrapper?.dataset?.configuratorFlockingMode || 'jersey';
    this.customInputNumber = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-number]',
    );
    this.customInputName = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-name]',
    );
    this.customInputNameErrorMessage = this.customInputWrapper?.querySelector(
      '[data-configurator-name-error-message]',
    );
    this.customInputLimitCharcount = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-limit-charcount]',
    );
    this.customInputBlacklistErrorMessage = this.customInputWrapper?.querySelector(
      '[data-configurator-blacklist-error-message]',
    );

    this.logosWrapper = this.querySelector('[data-configurator-logos-wrapper]');
    this.logosOptions = this.logosWrapper?.querySelectorAll('[data-configurator-logos-option]');
    this.logosValueInput = this.logosWrapper?.querySelector('[data-configurator-logos-value]');

    this.badgeInputCheckbox = this.querySelector('[data-configurator-option-input-badge]');

    this.additionalProductElements = this.querySelectorAll(
      '[data-configurator-additional-product]',
    );

    this.priceRegularElement = this.querySelector('[data-configurator-price-regular]');
    this.priceRegular = parseInt(this.priceRegularElement.dataset.configuratorPriceRegularValue);
    this.priceConfiguration = this.priceRegular;

    // Check if compare at price is available
    this.priceCompareElement = this.querySelector('[data-configurator-price-compare]');
    if (this.priceCompareElement) {
      this.priceCompare = parseInt(this.priceCompareElement.dataset.configuratorPriceCompareValue);
      this.priceConfigurationCompare = this.priceCompare;
    }

    // Check for prefill flocking URL parameters
    this.flockingUrlParameters = this.getFlockingUrlParameters();
    if (this.flockingUrlParameters.playerNumber) {
      this.handlePlayerFlockingPrefill(this.flockingUrlParameters.playerNumber);
    } else if (this.flockingUrlParameters.individualName) {
      this.handleCustomFlockingPrefill(this.flockingUrlParameters.individualName);
    }

    //  Bind events
    this.bindEvents();
  }

  connectedCallback() {
    this.startNamePreviewFitLifecycle();
  }

  disconnectedCallback() {
    this.stopNamePreviewFitLifecycle();
  }

  startNamePreviewFitLifecycle = () => {
    this.stopNamePreviewFitLifecycle();
    const lifecycleId = ++this.nameFitLifecycleId;

    if (typeof ResizeObserver !== 'undefined') {
      this.nameFitResizeObserver = new ResizeObserver(this.scheduleNamePreviewFit);
      const stage = this.productMediaElement?.querySelector('[data-product-media-stage]');
      if (stage) this.nameFitResizeObserver.observe(stage);
      this.getAllPreviewElements().name.forEach((nameElement) => {
        this.nameFitResizeObserver.observe(nameElement);
      });
    }

    document.fonts?.addEventListener('loadingdone', this.scheduleNamePreviewFit);
    document.fonts?.ready.then(() => {
      if (this.isConnected && this.nameFitLifecycleId === lifecycleId) {
        this.scheduleNamePreviewFit();
      }
    });
    this.scheduleNamePreviewFit();
  };

  stopNamePreviewFitLifecycle = () => {
    this.nameFitLifecycleId += 1;
    this.nameFitResizeObserver?.disconnect();
    this.nameFitResizeObserver = null;
    document.fonts?.removeEventListener('loadingdone', this.scheduleNamePreviewFit);
    if (this.nameFitAnimationFrame !== null) {
      window.cancelAnimationFrame(this.nameFitAnimationFrame);
      this.nameFitAnimationFrame = null;
    }
  };

  getFlockingUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
      individualName: urlParams.get('individual_name'),
      playerNumber: urlParams.get('player_number'),
    };
  };

  /**
   * Programmatically prefills the custom flocking option with a given name
   * This method simulates user interaction by selecting the custom print option
   * and setting the name input field, triggering all necessary events and UI updates
   *
   * @param {string} name - The custom name to prefill (max 14 characters)
   */
  handleCustomFlockingPrefill = (name) => {
    // Find the custom print option radio button from available options
    const control = Array.from(this.printOptions).find((option) => option.value === 'custom');

    // Process and clean the input name, ensuring it's a valid string
    const nameProcessed = name && typeof name === 'string' ? name.trim() : '';

    // Validate name length (must be between 1-14 characters)
    const isValidName = nameProcessed.length > 0 && nameProcessed.length <= 14;

    // Return if there is no control, an invalid name or no input
    if (!control || !isValidName || !this.customInputName) {
      return;
    }

    // Check the custom print option radio button
    control.checked = true;

    // Trigger the print option selection logic (shows custom input wrapper, etc.)
    this.handlePrintOptionInput({ target: control });

    // Set the processed name in the input field
    this.customInputName.value = nameProcessed;

    // Prefill runs before event listeners are bound, so validate directly.
    this.handleCustomNameInput();
  };

  /**
   * Programmatically prefills the player flocking option with a specific player
   * This method simulates user interaction by selecting the player print option
   * and choosing a player from the autocomplete options
   *
   * @param {string} number - The player number to select from available options
   */
  handlePlayerFlockingPrefill = (number) => {
    // Find the player print option radio button from available options
    const control = Array.from(this.printOptions).find((option) => option.value === 'player');

    // Check if the provided player number exists in the available player options
    const playerNumberExists = this.playerInput?.allOptions.some(
      (option) => option.number === number,
    );

    // Return if there is no control, no matching player number or no input
    if (!control || !playerNumberExists || !this.playerInput) {
      return;
    }

    // Check the player print option radio button
    control.checked = true;

    // Trigger the print option selection logic (shows player input wrapper, etc.)
    this.handlePrintOptionInput({ target: control });

    // Find the complete player data object using the number
    const player = this.playerInput.allOptions.find((option) => option.number === number);

    if (player) {
      const playerId = player.id;

      // Handle the player selection (updates preview, configuration values)
      this.handlePlayerFlockingChange(player, this.playerInput);

      // Recalculate the total price with the selected player's price
      this.calculateNewTotalPrice();

      // Update the autocomplete UI to show the selected player
      this.playerInput.setOption(playerId);
    }
  };

  /**
   * Bind the elements
   */
  bindEvents = () => {
    document.addEventListener('variant-change-complete', this.handleVariantChange.bind(this));
    this.productMediaElement?.addEventListener(
      'product-media:change',
      this.handleProductMediaChange.bind(this),
    );

    customElements.whenDefined('product-media').then(() => {
      this.productMediaElement = this.querySelector('product-media');
      this.handleProductMediaChange({
        detail: { role: this.productMediaElement?.selectedMediaRole || '' },
      });
    });

    // Handle printOption radio input change
    this.printOptions?.forEach((printOption) => {
      printOption.addEventListener('change', this.handlePrintOptionInput.bind(this));
    });
    this.printAcknowledgementInput?.addEventListener(
      'change',
      this.handlePrintAcknowledgementInput.bind(this),
    );

    // Handle logoOption checkbox input change
    this.logosOptions?.forEach((logosOption) => {
      logosOption.addEventListener('change', this.handleLogoOptionInput.bind(this));
    });

    this.autocompleteInputs?.forEach((autocompleteInput) => {
      autocompleteInput.addEventListener(
        'autocomplete:change',
        this.handleAutocompleteChange.bind(this),
      );
      autocompleteInput.addEventListener(
        'autocomplete:clear',
        this.handleAutocompleteClear.bind(this),
      );
    });

    this.customInputNumber?.addEventListener('input', this.handleCustomNumberInput.bind(this));
    this.customInputName?.addEventListener('input', this.handleCustomNameInput.bind(this));
    this.playerInputOptions?.forEach((playerInputOption) => {
      playerInputOption.addEventListener('click', (event) => {
        event.preventDefault();
        this.handlePlayerInput(playerInputOption);
      });
      // Add keyboard support for player options
      playerInputOption.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handlePlayerInput(playerInputOption);
        }
      });
    });
    this.productFormElement
      .querySelector('form')
      .addEventListener('submit', this.handleAddToCart.bind(this));
  };

  /**
   * Handle variant change
   */
  handleVariantChange = () => {
    this.priceRegularElement = this.querySelector('[data-configurator-price-regular]');
    this.priceCompareElement = this.querySelector('[data-configurator-price-compare]');

    this.calculateNewTotalPrice();
    this.updatePrintAcknowledgementAddButtonState();
  };

  /**
   * Keep the single overlay stack scoped to the selected configurable view.
   * @param {CustomEvent|Object} event
   */
  handleProductMediaChange = (event) => {
    this.activeMediaRole = event.detail?.role || '';
    this.mediaWrapper.querySelectorAll('[data-configurator-preview-role]').forEach((element) => {
      element.hidden = element.dataset.configuratorPreviewRole !== this.activeMediaRole;
    });
    this.syncPreviewLogos();
    this.scheduleNamePreviewFit();
  };

  /**
   * Handle change event on the print options input
   */
  handlePrintOptionInput = (event) => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    this.togglePrintAcknowledgement(false, true);

    // Hide all custom inputs by default
    this.playerInputWrapper?.classList.add('hidden');
    this.customInputWrapper?.classList.add('hidden');

    // Reset the preview
    this.resetPreview();

    // Guard: Don't show any inputs if print option value is none.
    if (event.target.value === 'none') {
      this.updateConfigurationValuePrint('', '');
      this.calculateNewTotalPrice();
      this.updatePrintAcknowledgementAddButtonState();
      return;
    }

    // Actions based on input choice
    switch (event.target.value) {
      case 'player':
        this.togglePrintAcknowledgement(true, true);

        // Show the player input element
        this.playerInputWrapper.classList.remove('hidden');

        // Set focus to the player selection dropdown button
        setTimeout(() => {
          if (this.playerInputButton) {
            this.playerInputButton.click();
          }

          // Focus the first player item
          const firstPlayerItem = this.playerInputSelect?.querySelector('a');
          if (firstPlayerItem) {
            firstPlayerItem.focus();
          }
        }, 100);

        // Check if player is filled in already and add this ID already OR if this is the first time, remove any value
        if (this.playerInput.dataset.configuratorPlayerInputId !== '') {
          this.togglePreviewElementClublogo(true);
          this.renderNumbersPreview(this.playerInput.dataset.configuratorPlayerInputNumber);
          this.renderNamePreview(this.playerInput.dataset.configuratorPlayerInputName);

          this.updateConfigurationValuePrint(
            this.playerInput.dataset.configuratorPlayerInputId,
            this.playerInput.dataset.configuratorPlayerInputPrice,
          );
        } else {
          this.updateConfigurationValuePrint('', '');
        }

        // Calculate the new total price
        this.calculateNewTotalPrice();

        break;
      case 'custom':
        this.togglePrintAcknowledgement(true, true);

        // Show the custom input element
        this.customInputWrapper.classList.remove('hidden');

        // Set focus to the first custom input field (jersey number)
        setTimeout(() => {
          if (this.customInputNumber) {
            this.customInputNumber.focus();
          }
        }, 100);

        // Add custom print product variant ID to element
        this.updateConfigurationValuePrint(
          event.target.dataset.configuratorPrintOptionCustomId,
          parseInt(event.target.dataset.configuratorPrintOptionCustomPrice),
        );

        // Calculate the new total price
        this.calculateNewTotalPrice();

        break;
    }
    this.updatePrintAcknowledgementAddButtonState();
  };

  /**
   * Handle change event on the logo options input
   * Rules:
   * - None + Badge = exclusive (selecting one deselects the other)
   * - None + Logo = exclusive (selecting one deselects the other)
   * - Logo + Badge = NOT exclusive (can have both)
   * - Logo + Logo = exclusive (only one logo at a time)
   * - Clicking None deselects everything
   * @param {Event} event
   */
  handleLogoOptionInput = (event) => {
    // Logos and the badge are only rendered on the front of the shirt
    this.slideToByAltName('front');

    const isBadge = event.target.hasAttribute('data-configurator-option-input-badge');
    const isNone = event.target.value === 'none';
    const isLogo = !isBadge && !isNone;

    // Handle "none" option: deselect ALL (logos AND badge)
    if (isNone) {
      this.logosOptions.forEach((option) => {
        option.checked = option.value === 'none';
      });
      this.syncPreviewLogos();
      this.rebuildLogosValue();
      this.calculateNewTotalPrice();
      return;
    }

    // Handle logo selection: uncheck None, uncheck other logos (badge stays as is)
    if (isLogo && event.target.checked) {
      this.logosOptions[0].checked = false; // Uncheck "none"
      this.logosOptions.forEach((option) => {
        // Uncheck other logos (not badge, not none, not the selected one)
        if (
          !option.hasAttribute('data-configurator-option-input-badge') &&
          option.value !== 'none' &&
          option !== event.target
        ) {
          option.checked = false;
        }
      });
    }

    // Handle badge selection: uncheck None (logo stays as is)
    if (isBadge && event.target.checked) {
      this.logosOptions[0].checked = false;
    }

    // Handle logo deselection: if no logo AND no badge selected, check None
    if (isLogo && !event.target.checked) {
      const hasLogoSelected = Array.from(this.logosOptions).some(
        (option) =>
          option.checked &&
          !option.hasAttribute('data-configurator-option-input-badge') &&
          option.value !== 'none',
      );
      const hasBadgeSelected = this.badgeInputCheckbox?.checked;
      if (!hasLogoSelected && !hasBadgeSelected) {
        this.logosOptions[0].checked = true;
      }
    }

    // Handle badge deselection: if no logo selected, check None
    if (isBadge && !event.target.checked) {
      const hasLogoSelected = Array.from(this.logosOptions).some(
        (option) =>
          option.checked &&
          !option.hasAttribute('data-configurator-option-input-badge') &&
          option.value !== 'none',
      );
      if (!hasLogoSelected) {
        this.logosOptions[0].checked = true;
      }
    }

    // Update previews
    this.syncPreviewLogos();

    // Rebuild the logos value from current state
    this.rebuildLogosValue();
    this.calculateNewTotalPrice();
  };

  /**
   * Rebuild the logos value input from currently checked options
   */
  rebuildLogosValue = () => {
    const checkedOptions = this.logosWrapper.querySelectorAll(
      '[data-configurator-logos-option]:checked',
    );
    let values = [];
    let prices = [];

    checkedOptions.forEach((option) => {
      if (option.value !== 'none') {
        values.push(option.value);
        prices.push(option.dataset.configuratorLogosOptionPrice);
      }
    });

    this.updateConfigurationValueLogos(
      values.length ? ',' + values.join(',') : '',
      prices.length ? ',' + prices.join(',') : '',
    );
  };

  /**
   * Handle player input
   * @param {Object} target
   */
  handlePlayerInput = (target) => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    // Remove error class if applicable
    this.playerInputButton.classList.remove('border-danger');
    this.playerInputButton.classList.add('border-gray-3');

    // Render the previews
    this.togglePreviewElementClublogo(true);
    const isTextOnlyFlocking = target.dataset.configuratorPlayerInputOptionIsTextOnly == 'true';
    if (isTextOnlyFlocking) {
      this.renderNumbersPreview('');
      this.renderNamePreview('');
    } else {
      this.renderNumbersPreview(target.dataset.configuratorPlayerInputOptionNumber);
      this.renderNamePreview(target.dataset.configuratorPlayerInputOptionName);
    }

    // Update the custom inputs button value and close the dropdown
    this.playerInputButton.querySelector('.field__dropdown-label').innerHTML = target.innerHTML;
    this.playerInputSelect.classList.add('field__dropdown--hidden');

    // Update the player input id + data
    this.playerInput.dataset.configuratorPlayerInputId =
      target.dataset.configuratorPlayerInputOption;
    this.playerInput.dataset.configuratorPlayerInputPrice =
      target.dataset.configuratorPlayerInputOptionPrice;
    this.playerInput.dataset.configuratorPlayerInputNumber =
      target.dataset.configuratorPlayerInputOptionNumber;
    this.playerInput.dataset.configuratorPlayerInputName =
      target.dataset.configuratorPlayerInputOptionName;
    this.playerInput.dataset.configuratorPlayerInputIsTextOnly = isTextOnlyFlocking;
    this.updateConfigurationValuePrint(
      target.dataset.configuratorPlayerInputOption,
      target.dataset.configuratorPlayerInputOptionPrice,
    );

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle custom number input
   * Works for both 'jersey' mode (number + name) and 'number_only' mode (pants)
   */
  handleCustomNumberInput = () => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    // Remove any field errors if applicable
    this.customInputNumber.classList.remove('field__invalid');

    this.updateCustomInputValidation();
    this.updatePrintAcknowledgementAddButtonState();

    // Guard: return if input is empty
    if (!this.customInputNumber.value.match(/^[0-9]+$/)) {
      this.customInputNumber.value = '';
      this.updateCustomInputValidation();
      this.updatePrintAcknowledgementAddButtonState();

      const allElements = this.getAllPreviewElements();
      allElements.numbers.forEach((numbersElement) => {
        if (numbersElement) {
          numbersElement.innerHTML = '';
        }
      });

      // Check: if custom input name is also empty (or doesn't exist for number_only mode), remove logo
      if (this.flockingMode === 'number_only' || this.customInputName?.value.length <= 0) {
        this.togglePreviewElementClublogo(false);
      }
      return;
    }

    // Guard: check if the value isn't on the blacklist
    if (this.getCustomInputValidation().numberInvalid) {
      this.renderNumbersPreview('');
      return;
    }

    // Render the previews
    this.togglePreviewElementClublogo(true);
    this.renderNumbersPreview(this.customInputNumber.value);
  };

  /**
   * Handle custom name input
   */
  handleCustomNameInput = () => {
    // Slide to the back of the shirt
    this.slideToByAltName('back');

    // Remove any field errors if applicable
    this.customInputName.classList.remove('field__invalid');

    this.updateCustomInputValidation();
    this.updatePrintAcknowledgementAddButtonState();

    // Check: if customInputName is empty, return
    if (this.customInputName.value.length <= 0) {
      this.renderNamePreview('');

      // Check: if number is also empty, remove logo
      if (this.customInputNumber.value.length <= 0) {
        this.togglePreviewElementClublogo(false);
      }

      // Update the custom input charcount
      this.updateCustomInputCharcount(0);
      return;
    }

    // Guard: check if the value isn't on the blacklist
    if (this.getCustomInputValidation().nameInvalid) {
      this.renderNamePreview('');
      this.updateCustomInputCharcount(this.customInputName.value.length);
      return;
    }

    // Render the previews
    this.togglePreviewElementClublogo(true);
    this.renderNamePreview(this.customInputName.value);

    // Update the custom input charcount
    this.updateCustomInputCharcount(this.customInputName.value.length);
  };

  /**
   * Update custom input limit character count
   * Only applies to jersey mode with name field
   * @param {Number} count
   */
  updateCustomInputCharcount = (count) => {
    if (this.customInputLimitCharcount) {
      this.customInputLimitCharcount.innerHTML = count;
    }
  };

  /**
   * Handle autocomplete input selection for flocking options
   * @param {CustomEvent} event
   */
  handleAutocompleteChange = (event) => {
    const { option } = event.detail;
    const autocompleteElement = event.target;
    const flockingType = autocompleteElement.dataset.configuratorFlockingType;

    // Slide to the back of the shirt to show flocking
    this.slideToByAltName('back');

    // Handle different types of flocking
    switch (flockingType) {
      case 'district':
      case 'location':
      case 'player':
        this.handlePlayerFlockingChange(option, autocompleteElement);
        break;
      default:
        console.warn('Unknown flocking type:', flockingType);
    }

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle autocomplete input clearing for flocking options
   * @param {CustomEvent} event
   */
  handleAutocompleteClear = (event) => {
    const autocompleteElement = event.target;
    const flockingType = autocompleteElement.dataset.configuratorFlockingType;

    // Handle different types of flocking
    switch (flockingType) {
      case 'district':
      case 'location':
      case 'player':
        this.handlePlayerFlockingClear(autocompleteElement);
        break;
      default:
        console.warn('Unknown flocking type:', flockingType);
    }

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle player flocking selection (alternative to dropdown)
   * @param {Object} option
   * @param {HTMLElement} autocompleteElement
   */
  handlePlayerFlockingChange = (option, autocompleteElement) => {
    autocompleteElement
      ?.querySelector('[data-autocomplete-input]')
      ?.classList.remove('field__invalid');

    // Render the previews similar to existing player input
    this.togglePreviewElementClublogo(true);

    const isTextOnlyFlocking = option.isTextOnly === true || option.isTextOnly === 'true';
    if (isTextOnlyFlocking) {
      this.renderNumbersPreview('');
      this.renderNamePreview('');
    } else {
      this.renderNumbersPreview(option.number || '');
      this.renderNamePreview(option.name);
    }

    // Update the configuration value for printing
    this.updateConfigurationValuePrint(option.id, option.price || 0);

    // Update the player input id + data
    this.playerInput.dataset.configuratorPlayerInputId = option.id;
    this.playerInput.dataset.configuratorPlayerInputPrice = option.price || 0;
    this.playerInput.dataset.configuratorPlayerInputNumber = option.number || '';
    this.playerInput.dataset.configuratorPlayerInputName = option.name || '';
    this.playerInput.dataset.configuratorPlayerInputIsTextOnly = isTextOnlyFlocking;
  };

  /**
   * Handle player flocking clearing
   * @param {HTMLElement} autocompleteElement
   */
  handlePlayerFlockingClear = (autocompleteElement) => {
    // Clear the previews
    this.renderNumbersPreview('');
    this.renderNamePreview('');
    this.togglePreviewElementClublogo(false);

    // Clear the configuration value
    this.updateConfigurationValuePrint('', '');

    // Clear the player input data
    this.playerInput.dataset.configuratorPlayerInputId = '';
    this.playerInput.dataset.configuratorPlayerInputPrice = '';
    this.playerInput.dataset.configuratorPlayerInputNumber = '';
    this.playerInput.dataset.configuratorPlayerInputName = '';
    this.playerInput.dataset.configuratorPlayerInputIsTextOnly = '';
  };

  /**
   * Navigate to image by alt name
   * @param {String} altName
   */
  slideToByAltName = (altName) => {
    const reason = altName === 'back' ? 'print' : 'logo';
    const selectRole = () => this.productMediaElement?.selectMediaByRole?.(altName, reason);

    if (this.productMediaElement?.selectMediaByRole) {
      selectRole();
    } else {
      customElements.whenDefined('product-media').then(() => {
        this.productMediaElement = this.querySelector('product-media');
        selectRole();
      });
    }
  };

  /**
   * Render the numbers preview
   * For 'jersey' mode: Renders as digit images
   * For 'number_only' mode: Renders as text with Hertha font (like names)
   * @param {String} input
   */
  renderNumbersPreview = (input) => {
    const allElements = this.getAllPreviewElements();

    allElements.numbers.forEach((numbersElement) => {
      if (!numbersElement) return;

      // For number_only mode, render the preview as text
      if (this.flockingMode === 'number_only') {
        numbersElement.textContent = input;
      } else {
        numbersElement.replaceChildren();

        for (let i = 0; i < input.length; i++) {
          const numberImageElement = document.createElement('img');
          numberImageElement.setAttribute('src', this.options.numberImageSources[input[i]]);
          numberImageElement.setAttribute('alt', '');
          numberImageElement.setAttribute(
            'class',
            'block h-full w-auto max-w-[49%] object-contain',
          );

          numbersElement.appendChild(numberImageElement);
        }
      }
    });
  };

  /**
   * Render the preview
   * @param {String} input
   */
  renderNamePreview = (input) => {
    const allElements = this.getAllPreviewElements();

    allElements.name.forEach((nameElement) => {
      if (nameElement) {
        const textElement =
          nameElement.querySelector('[data-configurator-media-image-preview-name-text]') ||
          nameElement;
        textElement.textContent = input;
      }
    });
    this.scheduleNamePreviewFit();
  };

  scheduleNamePreviewFit = () => {
    if (!this.isConnected) return;
    if (this.nameFitAnimationFrame !== null) {
      window.cancelAnimationFrame(this.nameFitAnimationFrame);
    }

    this.nameFitAnimationFrame = window.requestAnimationFrame(() => {
      this.nameFitAnimationFrame = null;
      if (!this.isConnected) return;
      this.getAllPreviewElements().name.forEach((nameElement) => {
        this.fitNamePreview(nameElement);
      });
    });
  };

  /** Keep exceptionally wide valid names inside the authored print width. */
  fitNamePreview = (nameElement) => {
    const textElement =
      nameElement?.querySelector('[data-configurator-media-image-preview-name-text]') ||
      nameElement;
    if (!nameElement || !textElement) return;

    nameElement.style.setProperty('--name-fit-scale', '1');
    const availableWidth = nameElement.clientWidth;
    const textWidth = textElement.getBoundingClientRect().width;
    const scale = availableWidth > 0 && textWidth > availableWidth ? availableWidth / textWidth : 1;
    nameElement.style.setProperty('--name-fit-scale', String(scale));
  };

  /**
   * Toggle the preview clublogo element
   * @param {Boolean} showBoolean
   */
  togglePreviewElementClublogo = (showBoolean) => {
    const allElements = this.getAllPreviewElements();

    // Update all clublogo preview elements
    allElements.clublogo.forEach((clublogoElement) => {
      if (clublogoElement) {
        // Check if media image preview logo needs to be shown or hidden
        if (showBoolean) {
          clublogoElement.classList.remove('hidden');
          // Update the configuration value using the first element (they should all have the same data)
          if (clublogoElement === allElements.clublogo[0]) {
            this.updateConfigurationValueClublogoPrint(
              clublogoElement.dataset.configuratorMediaImagePreviewClublogoValue,
            );
          }
        } else {
          clublogoElement.classList.add('hidden');
          // Update the configuration value using the first element
          if (clublogoElement === allElements.clublogo[0]) {
            this.updateConfigurationValueClublogoPrint('');
          }
        }
      }
    });
  };

  /**
   * Show the chosen logos on the preview
   * @param {String} logoId
   */
  showPreviewLogos = (logoId) => {
    const allElements = this.getAllPreviewElements();

    allElements.logos.forEach((logoElement) => {
      if (
        logoElement &&
        logoElement.dataset.configuratorLogoValue === logoId &&
        logoElement.dataset.configuratorOverlayRole === this.productMediaElement?.selectedMediaRole
      ) {
        logoElement.classList.remove('hidden');
      }
    });
  };

  /**
   * Hide all logos on the preview
   */
  hidePreviewLogos = () => {
    const allElements = this.getAllPreviewElements();

    // Hide all logo preview elements
    allElements.logos.forEach((logoElement) => {
      if (logoElement) {
        logoElement.classList.add('hidden');
      }
    });
  };

  /**
   * Toggle the members badge on the preview
   * @param {Boolean} showBoolean
   */
  togglePreviewBadge = (showBoolean) => {
    const allElements = this.getAllPreviewElements();

    allElements.badge.forEach((badgeElement) => {
      if (badgeElement) {
        if (
          showBoolean &&
          badgeElement.dataset.configuratorOverlayRole ===
            this.productMediaElement?.selectedMediaRole
        ) {
          badgeElement.classList.remove('hidden');
        } else {
          badgeElement.classList.add('hidden');
        }
      }
    });
  };

  /**
   * Sync the logo and badge previews with the currently checked options.
   * The previews are always rebuilt from the checkbox state so options that were
   * unchecked programmatically (e.g. by selecting "none") lose their preview too.
   */
  syncPreviewLogos = () => {
    this.hidePreviewLogos();
    this.togglePreviewBadge(false);

    this.logosWrapper
      ?.querySelectorAll('[data-configurator-logos-option]:checked')
      .forEach((checkedOption) => {
        if (checkedOption.hasAttribute('data-configurator-option-input-badge')) {
          this.togglePreviewBadge(true);
        } else if (checkedOption.value !== 'none') {
          this.showPreviewLogos(checkedOption.value);
        }
      });
  };

  /**
   * Update configuration value - Clublogo print
   * @param {String} configurationClublogoPrintValue
   */
  updateConfigurationValueClublogoPrint = (configurationClublogoPrintValue) => {
    if (this.printClublogoValueInput) {
      this.printClublogoValueInput.value = configurationClublogoPrintValue;
    }
  };

  /**
   * Update configuration value - Print
   * @param {String} configurationPrintValue
   * @param {Number} configurationPrintPrice
   */
  updateConfigurationValuePrint = (configurationPrintValue, configurationPrintPrice) => {
    this.printValueInput.value = configurationPrintValue;
    this.printValueInput.dataset.configuratorPrintPrice = configurationPrintPrice;
  };

  /**
   * Update configuration value - Logos
   * @param {String} configurationLogosValue
   */
  updateConfigurationValueLogos = (configurationLogosValue, configurationLogosPrice) => {
    this.logosValueInput.value = configurationLogosValue;
    this.logosValueInput.dataset.configuratorLogosPrice = configurationLogosPrice;
  };

  /** Map Unicode decimal digit blocks to their ASCII equivalents for matching only. */
  normalizeBlacklistDigit = (digit) => {
    const codePoint = digit.codePointAt(0);
    let blockStart = codePoint;
    while (/\p{Nd}/u.test(String.fromCodePoint(blockStart - 1))) blockStart -= 1;
    return String((codePoint - blockStart) % 10);
  };

  /** Fold German transliterations, separators and invisible characters for matching only. */
  normalizeBlacklistName = (value = '') =>
    value
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]|\p{Default_Ignorable_Code_Point}/gu, '')
      .replace(/ı/g, 'i')
      .replace(/ß/g, 'ss')
      .replace(/([aou])e/g, '$1')
      .replace(/\p{Nd}/gu, this.normalizeBlacklistDigit);

  /** Keep numeric blacklist entries consistent between the number and name fields. */
  getBlacklistNumbers = () =>
    this.options.blacklistNumbers
      .map((entry) => entry.trim())
      .filter((entry) => /^[0-9]+$/.test(entry))
      .map((entry) => String(Number(entry)));

  isValidCustomName = (name) => {
    const letter = String.raw`(?=\p{Script=Latin})\p{L}`;
    const mark = String.raw`(?=[\p{Script_Extensions=Latin}\p{Script=Inherited}])\p{M}`;
    const word = `(?:${letter}(?:${mark})*)+`;
    return (
      !/\p{Default_Ignorable_Code_Point}/u.test(name) &&
      new RegExp(`^${word}(?:[-'’]${word})*$(?![\\s\\S])`, 'u').test(name.normalize('NFC'))
    );
  };

  getCustomInputValidation = () => {
    const number = this.customInputNumber?.value || '';
    const name = this.customInputName?.value || '';
    const normalizedName = this.normalizeBlacklistName(name);
    const numberValid = /^[0-9]{1,2}$/.test(number);
    const blacklistNumbers = this.getBlacklistNumbers();
    const numberBlocked = numberValid && blacklistNumbers.includes(String(Number(number)));
    const nameBlocked =
      this.flockingMode !== 'number_only' &&
      normalizedName !== '' &&
      (this.options.blacklistNames.some((entry) => {
        const normalizedEntry = this.normalizeBlacklistName(entry);
        return normalizedEntry !== '' && normalizedName.includes(normalizedEntry);
      }) ||
        blacklistNumbers.some((entry) => normalizedName.includes(entry)));
    const numberInvalid = !numberValid || numberBlocked;
    const nameFormatInvalid = this.flockingMode !== 'number_only' && !this.isValidCustomName(name);
    const nameInvalid =
      this.flockingMode !== 'number_only' &&
      (nameFormatInvalid ||
        !normalizedName ||
        (this.customInputName?.maxLength > 0 && name.length > this.customInputName.maxLength) ||
        nameBlocked);
    return {
      numberBlocked,
      nameFormatInvalid,
      nameBlocked,
      numberInvalid,
      nameInvalid,
      invalid: numberInvalid || nameInvalid,
    };
  };

  /** Revalidate both fields together so one input cannot clear the other's errors. */
  updateCustomInputValidation = (showRequired = false) => {
    const validation = this.getCustomInputValidation();
    const active = this.getSelectedPrintValue() === 'custom';
    this.toggleBlacklistErrorMessage(
      active && (validation.numberBlocked || validation.nameBlocked),
    );
    this.customInputNameErrorMessage?.classList.toggle(
      'hidden',
      !(
        active &&
        validation.nameFormatInvalid &&
        (showRequired || this.customInputName?.value.length > 0)
      ),
    );
    [
      [this.customInputNumber, validation.numberInvalid],
      [this.customInputName, validation.nameInvalid],
    ].forEach(([input, invalid]) => {
      if (!input) return;
      const showError = active && invalid && (showRequired || input.value.length > 0);
      input.classList.toggle('field__invalid', showError);
      input.setAttribute('aria-invalid', showError ? 'true' : 'false');
    });
    return validation;
  };

  /**
   * Toggle the blacklist error message
   * @param {Boolean} showBoolean
   */
  toggleBlacklistErrorMessage = (showBoolean = false) => {
    this.customInputBlacklistErrorMessage?.classList.toggle('hidden', !showBoolean);
  };

  /**
   * Handle change event on the print acknowledgement checkbox
   */
  handlePrintAcknowledgementInput = () => {
    this.togglePrintAcknowledgementError(false);
    this.updatePrintAcknowledgementAddButtonState();
  };

  /**
   * Get currently selected print option value
   * @return {String}
   */
  getSelectedPrintValue = () => {
    return (
      this.printWrapper?.querySelector('[data-configurator-print-option]:checked')?.value || 'none'
    );
  };

  /**
   * Check if the selected print option needs acknowledgement
   * @return {Boolean}
   */
  selectedPrintRequiresAcknowledgement = () => {
    return ['player', 'custom'].includes(this.getSelectedPrintValue());
  };

  /**
   * Check if the currently selected print is a text-only flocking.
   * A text-only flocking uses the same SKU as the default club-logo product,
   * so the default club-logo item must be skipped to avoid adding a duplicate.
   * @return {Boolean}
   */
  selectedPrintIsTextOnly = () => {
    return (
      this.getSelectedPrintValue() === 'player' &&
      this.playerInput?.dataset.configuratorPlayerInputIsTextOnly === 'true'
    );
  };

  /**
   * Toggle the print acknowledgement field
   * @param {Boolean} showBoolean
   * @param {Boolean} resetBoolean
   */
  togglePrintAcknowledgement = (showBoolean = false, resetBoolean = false) => {
    if (!this.printAcknowledgementWrapper) {
      return;
    }

    this.printAcknowledgementWrapper.classList.toggle('hidden', !showBoolean);

    if (resetBoolean && this.printAcknowledgementInput) {
      this.printAcknowledgementInput.checked = false;
    }

    if (!showBoolean || resetBoolean) {
      this.togglePrintAcknowledgementError(false);
    }

    this.updatePrintAcknowledgementAddButtonState();
  };

  /**
   * Toggle the print acknowledgement error state
   * @param {Boolean} showBoolean
   */
  togglePrintAcknowledgementError = (showBoolean = false) => {
    this.printAcknowledgementError?.classList.toggle('hidden', !showBoolean);
    this.printAcknowledgementInput?.setAttribute('aria-invalid', showBoolean ? 'true' : 'false');
  };

  selectedVariantUnavailable = () =>
    this.variantRadiosElement &&
    'currentVariant' in this.variantRadiosElement &&
    !this.variantRadiosElement.currentVariant?.available;

  /**
   * Keep blacklist, required fields, acknowledgement, stock and loading guards together
   */
  updatePrintAcknowledgementAddButtonState = () => {
    const submitButton = this.productFormElement?.querySelector('[name="add"]');
    if (!submitButton) {
      return;
    }

    const customValidation = this.updateCustomInputValidation();
    const shouldBlock =
      (this.selectedPrintRequiresAcknowledgement() &&
        this.printAcknowledgementInput?.checked !== true) ||
      (this.getSelectedPrintValue() === 'custom' && customValidation.invalid) ||
      this.selectedVariantUnavailable() ||
      submitButton.classList.contains('button--loading');

    if (shouldBlock) {
      submitButton.dataset.configuratorBlocked = 'true';
      submitButton.setAttribute('disabled', true);
    } else if (submitButton.dataset.configuratorBlocked === 'true') {
      delete submitButton.dataset.configuratorBlocked;
      submitButton.removeAttribute('disabled');
    }
  };

  /**
   * Reset preview
   */
  resetPreview = () => {
    // Remove the clublogo
    this.togglePreviewElementClublogo(false);

    // Clear the custom inputs
    if (this.customInputNumber) this.customInputNumber.value = '';
    this.renderNumbersPreview('');
    if (this.customInputName) this.customInputName.value = '';
    this.renderNamePreview('');
  };

  /**
   * Handle the add to cart call.
   */
  handleAddToCart = (event) => {
    event?.preventDefault();
    // Guard: if player / custom print is filled in correctly if applicable
    if (!this.canSubmitProductForm()) {
      return;
    }

    const submitButton = this.productFormElement.querySelector('[type="submit"]');
    const mainProductId = this.productFormElement.querySelector('[name=id]').value;
    const mainProductCustomPriceElement = this.productFormElement.querySelector(
      '[data-configurator-custom-member-price]',
    );
    const uniqueTimestamp = Date.now();
    const uniqueTimestampString = uniqueTimestamp.toString();

    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('button--loading');

    const sectionsToFetch = [
      ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
      ...this.cartNotification.getSectionsToRender().map((section) => section.id),
    ];

    let customPriceProperty = {};
    if (mainProductCustomPriceElement) {
      customPriceProperty = {
        _member_custom_price: mainProductCustomPriceElement.value,
      };
    }

    const allItems = [
      {
        id: mainProductId,
        quantity: 1,
        properties: {
          _masterID: uniqueTimestampString,
          ...customPriceProperty,
        },
      },
    ];

    // Push configuration product items if applicable
    allItems.push(...this.getConfigurationProductFormItems(uniqueTimestampString, mainProductId));

    // Check if there are additional products that need to be added in the cart.
    if (this.additionalProductElements.length > 0) {
      this.additionalProductElements.forEach((additionalProductElement) => {
        if (additionalProductElement.checked) {
          // Add additional product to the array
          allItems.push({
            id: additionalProductElement.dataset.configuratorAdditionalProduct,
            quantity: 1,
            properties: {},
          });
        }
      });
    }

    // Check: Remove masterID property if we are only submitting the plain jersey
    // if (allItems.length === 1) {
    //   delete allItems[0].properties;
    // }

    // Assign the body for the call
    const body = JSON.stringify({
      items: allItems,
      sections: sectionsToFetch,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_url}/add.js`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status == 422) {
          // Any 422 is surfaced as a quantity error, so log the real cause for debugging
          console.error('Cart add failed:', parsedState.description || parsedState.message);
          this.cartNotification.setActiveElement(document.activeElement);
          this.cartNotification.renderQuantityError(
            this.productFormElement.querySelector('[name=inventory_quantity]').value,
          );
          return;
        }

        this.cartDrawer.renderContents(parsedState);
        setTimeout(() => {
          this.cartDrawer.open(submitButton);
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        submitButton.classList.remove('button--loading');
        this.updatePrintAcknowledgementAddButtonState();
        if (submitButton.dataset.configuratorBlocked !== 'true') {
          submitButton.removeAttribute('disabled');
        }
      });
  };

  /**
   * Helper function to check if the product form can be submitted or needs to show errors to certain input fields
   * Validates based on flocking_mode: 'jersey' requires number + name, 'number_only' requires only number
   * @return {Boolean}
   */
  canSubmitProductForm = () => {
    const selectedPrintValue = this.getSelectedPrintValue();
    if (
      this.selectedVariantUnavailable() ||
      this.productFormElement?.querySelector('[name="add"]')?.classList.contains('button--loading')
    )
      return false;

    if (selectedPrintValue === 'player' || selectedPrintValue === 'custom') {
      if (
        selectedPrintValue === 'player' &&
        this.playerInput.dataset.configuratorPlayerInputId === ''
      ) {
        if (this.playerInputButton) {
          this.playerInputButton.classList.remove('border-gray-3');
          this.playerInputButton.classList.add('border-danger');
        } else {
          this.playerInput
            ?.querySelector('[data-autocomplete-input]')
            ?.classList.add('field__invalid');
        }
        return false;
      }
      if (selectedPrintValue === 'custom') {
        this.updatePrintAcknowledgementAddButtonState();
        const validation = this.updateCustomInputValidation(true);
        if (validation.invalid) return false;
      }

      if (this.selectedPrintRequiresAcknowledgement() && !this.printAcknowledgementInput?.checked) {
        this.togglePrintAcknowledgementError(true);
        return false;
      }
    }

    return true;
  };

  /**
   * Get all the additional configuration product items and return in array
   * @param {String} uniqueTimestampString
   * @param {String} parentProductId
   * @return {Object}
   */
  getConfigurationProductFormItems = (uniqueTimestampString, parentProductId) => {
    const configurationItems = [];

    // Check if clublogo is filled in.
    // Skip it for text-only flockings: they use the same SKU as the default
    // club-logo product, so adding both would duplicate the item in the cart.
    if (
      this.printClublogoValueInput &&
      this.printClublogoValueInput.value !== '' &&
      !this.selectedPrintIsTextOnly()
    ) {
      configurationItems.push({
        id: this.printClublogoValueInput?.value,
        quantity: 1,
        parent_id: parentProductId,
        properties: {
          _linkedID: uniqueTimestampString,
        },
      });
    }

    // Check if print value is filled in
    if (this.printValueInput && this.printValueInput.value !== '') {
      const selectedPrintValue = this.printWrapper?.querySelector(
        '[data-configurator-print-option]:checked',
      ).value;

      let customInputProperties = {};
      if (selectedPrintValue === 'custom') {
        // Handle different flocking modes for custom properties
        if (this.flockingMode === 'number_only') {
          // For pants/number-only products, only include the number
          customInputProperties['_print_value'] = `custom ["${this.customInputNumber.value}"]`;
        } else {
          // For jerseys, include both number and name
          customInputProperties['_print_value'] =
            `custom ["${this.customInputNumber.value} ${this.customInputName.value}"]`;
        }
        customInputProperties['_print_color'] = this.options.printColor;
      }

      configurationItems.push({
        id: this.printValueInput?.value,
        quantity: 1,
        parent_id: parentProductId,
        properties: {
          _linkedID: uniqueTimestampString,
          ...customInputProperties,
        },
      });
    }

    // Check if one or more logo values are filled in
    if (this.logosValueInput && this.logosValueInput.value !== '') {
      let logosValue = this.logosValueInput?.value || '';

      if (logosValue[0] == ',') {
        logosValue = logosValue.substring(1);
      }

      const logosValueArr = logosValue.split(',');

      logosValueArr.forEach((logoValue) => {
        // Never post an item without a variant id, it fails the whole add-to-cart call
        if (!logoValue) {
          return;
        }

        configurationItems.push({
          id: logoValue,
          quantity: 1,
          parent_id: parentProductId,
          properties: {
            _linkedID: uniqueTimestampString,
          },
        });
      });
    }

    return configurationItems;
  };

  /**
   * Get the total price by current configuration
   */
  calculateNewTotalPrice = () => {
    // Reset the calculation price before calculating it
    this.priceConfiguration = this.priceRegular;
    this.priceConfigurationCompare = this.priceCompare || null;

    // Check if print value is filled in
    if (this.printValueInput && this.printValueInput.dataset.configuratorPrintPrice !== '') {
      this.priceConfiguration =
        this.priceConfiguration + parseInt(this.printValueInput?.dataset.configuratorPrintPrice);
      if (this.priceConfigurationCompare !== null) {
        this.priceConfigurationCompare =
          this.priceConfigurationCompare +
          parseInt(this.printValueInput?.dataset.configuratorPrintPrice);
      }
    }

    // Check if one or more logo values are filled in
    if (this.logosValueInput && this.logosValueInput.value !== '') {
      let logosPrice = this.logosValueInput?.dataset.configuratorLogosPrice || '';

      if (logosPrice[0] == ',') {
        logosPrice = logosPrice.substring(1);
      }

      const logosPriceArr = logosPrice.split(',');

      logosPriceArr.forEach((logoPrice) => {
        this.priceConfiguration = this.priceConfiguration + parseInt(logoPrice);
        if (this.priceConfigurationCompare !== null) {
          this.priceConfigurationCompare = this.priceConfigurationCompare + parseInt(logoPrice);
        }
      });
    }

    // Update the total price
    this.updateTotalPrice();
  };

  /**
   * Update the total price of the configurator product.
   */
  updateTotalPrice = () => {
    // Update the price in the correct format
    this.priceRegularElement.innerHTML = formatMoney(
      this.priceConfiguration,
      window.shop.moneyFormat,
    );

    // Update the compare at price if available
    if (this.priceCompareElement) {
      this.priceCompareElement.innerHTML = formatMoney(
        this.priceConfigurationCompare,
        window.shop.moneyFormat,
      );
    }
  };

  /**
   * Get the preview elements from the single active overlay stack.
   */
  getAllPreviewElements = () => {
    return {
      clublogo: this.mediaWrapper.querySelectorAll(
        '[data-configurator-media-image-preview-clublogo]',
      ),
      numbers: this.mediaWrapper.querySelectorAll(
        '[data-configurator-media-image-preview-numbers]',
      ),
      name: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-name]'),
      logos: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-logo]'),
      badge: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-badge]'),
    };
  };
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
